<template>
  <div class="generic-node-config">
    <template v-if="schema && schema.properties">
      <n-form-item
        v-for="(prop, key) in schema.properties"
        :key="key"
        :label="formatLabel(key)"
      >
        <!-- String input -->
        <n-input
          v-if="prop.type === 'string' && !prop.enum"
          :value="modelValue[key]"
          @update:value="updateField(key, $event)"
          :placeholder="prop.description"
          :type="key.includes('secret') || key.includes('password') ? 'password' : 'text'"
        />

        <!-- String select (enum) -->
        <n-select
          v-else-if="prop.type === 'string' && prop.enum"
          :value="modelValue[key]"
          @update:value="updateField(key, $event)"
          :options="prop.enum.map(v => ({ label: formatLabel(v), value: v }))"
          :placeholder="prop.description"
        />

        <!-- Number input -->
        <n-input-number
          v-else-if="prop.type === 'integer' || prop.type === 'number'"
          :value="modelValue[key]"
          @update:value="updateField(key, $event)"
          :placeholder="prop.description"
          :min="prop.minimum"
          :max="prop.maximum"
          style="width: 100%"
        />

        <!-- Boolean switch -->
        <n-switch
          v-else-if="prop.type === 'boolean'"
          :value="modelValue[key]"
          @update:value="updateField(key, $event)"
        />

        <!-- Array input (simple strings) -->
        <n-dynamic-tags
          v-else-if="prop.type === 'array' && prop.items?.type === 'string'"
          :value="modelValue[key] || []"
          @update:value="updateField(key, $event)"
        />

        <!-- Object (JSON editor) -->
        <n-input
          v-else-if="prop.type === 'object'"
          :value="JSON.stringify(modelValue[key] || {}, null, 2)"
          @update:value="updateJsonField(key, $event)"
          type="textarea"
          :rows="4"
          placeholder="JSON object"
          font-family="monospace"
        />

        <!-- Description hint -->
        <template v-if="prop.description" #feedback>
          {{ prop.description }}
        </template>
      </n-form-item>
    </template>

    <!-- Fallback if no schema -->
    <n-empty v-else description="No configuration available" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  NFormItem, NInput, NInputNumber, NSelect, NSwitch,
  NDynamicTags, NEmpty
} from 'naive-ui'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  node: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])

// Get schema from node definition (would come from API in production)
const schema = computed(() => {
  try {
    return JSON.parse(props.node?.config_schema || '{}')
  } catch {
    return {}
  }
})

function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
}

function updateField(key, value) {
  emit('update:modelValue', {
    ...props.modelValue,
    [key]: value
  })
}

function updateJsonField(key, value) {
  try {
    const parsed = JSON.parse(value)
    updateField(key, parsed)
  } catch {
    // Invalid JSON, don't update
  }
}
</script>

<style scoped>
.generic-node-config {
  /* Styling handled by parent */
}
</style>
